// Shared HTTP plumbing: translates a non-2xx Response into the canonical
// ApiError shape (vouchers `parseErrorResponse` semantics). Wire format is
// RFC 9457 application/problem+json from kore::problem_details::ProblemDetails:
// `title` is the status's reason phrase, `detail` the server's message,
// `code` what copy keys on, `params`/`errors` what that copy reads. Parsed
// defensively: a proxy's body carries none of it.

import { ApiError, type FieldErrorSpec, NO_CODE } from "./error";

interface ProblemBody {
    title?: string;
    code?: string;
    detail?: string;
    instance?: string;
    requestId?: string;
    errors?: FieldErrorSpec[];
    params?: Record<string, string | null>;
}

export async function parseErrorResponse(res: Response): Promise<ApiError> {
    const requestIdHeader = res.headers.get("X-Request-Id") ?? undefined;
    const contentType = res.headers.get("Content-Type") ?? "";
    const isStructured =
        contentType.startsWith("application/problem+json") || contentType.startsWith("application/json");

    // Read the body as text once. JSON parsing branches off this string so the
    // raw text remains available for the opaque fallback even if JSON.parse fails.
    let text = "";
    try {
        text = await res.text();
    } catch {
        // Stream error (rare). Leave text empty; the ApiError below carries status only.
    }

    if (isStructured && text.length > 0) {
        try {
            const body = JSON.parse(text) as ProblemBody;
            return new ApiError({
                status: res.status,
                code: body.code ?? NO_CODE,
                title: body.title ?? undefined,
                detail: body.detail ?? undefined,
                instance: body.instance ?? undefined,
                errors: body.errors ?? undefined,
                params: body.params ?? undefined,
                // kore's middleware puts the request's id in the body; the
                // header covers a body without one.
                requestId: body.requestId ?? requestIdHeader,
            });
        } catch {
            // Body wasn't valid JSON despite the content-type — fall through to opaque.
        }
    }

    // Opaque body (HTML 503, raw proxy error, broken JSON despite structured CT).
    // Capture up to 2000 chars for forensic value — opaque bodies are often
    // multi-line HTML or JSON fragments where the first 200 chars aren't enough
    // to identify the root cause.
    return new ApiError({
        status: res.status,
        code: NO_CODE,
        detail: text.slice(0, 2000) || undefined,
        requestId: requestIdHeader,
    });
}
